/**
 * Post-case 5-point Likert survey — confidence + mental demand.
 *
 * Shown after a study case is signed. Keyboard-first: press 1–5 to pick
 * the active field's rating, Tab to switch fields, Enter to submit.
 *
 * Both ratings are required before Submit enables (a skip cannot happen
 * silently). If the rad closes without submitting, the hook caller
 * should emit a logged signal (`survey_skipped`) rather than swallowing
 * the event.
 *
 * Wire contract: both values are integers 1–5, sent via
 * `useSubmitReaderFeedback` / `POST /api/submit_reader_feedback`.
 * The backend is widening from 1–4 → 1–5 in parallel (#554 BE).
 */

import { useEffect, useRef, useState } from "react";

import { logger } from "../../utils/logger";

export interface StudyPostCaseSurveyProps {
  /** Called with both ratings on Submit. Async — UI stays open until resolved. */
  onSubmit: (confidence: number, mentalDemand: number) => Promise<void>;
  /**
   * Called when the rad presses Escape / Skip. Must NOT be a silent discard —
   * caller is responsible for logging a `survey_skipped` event.
   */
  onSkip: () => void;
}

type Field = "confidence" | "mentalDemand";

const CONFIDENCE_RATINGS: { value: number; label: string }[] = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "Slightly" },
  { value: 3, label: "Moderately" },
  { value: 4, label: "Very" },
  { value: 5, label: "Completely" },
];

const MENTAL_DEMAND_RATINGS: { value: number; label: string }[] = [
  { value: 1, label: "Very low" },
  { value: 2, label: "Low" },
  { value: 3, label: "Moderate" },
  { value: 4, label: "High" },
  { value: 5, label: "Very high" },
];

export const StudyPostCaseSurvey: React.FC<StudyPostCaseSurveyProps> = ({ onSubmit, onSkip }) => {
  const [confidence, setConfidence] = useState<number | null>(null);
  const [mentalDemand, setMentalDemand] = useState<number | null>(null);
  const [activeField, setActiveField] = useState<Field>("confidence");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track user-owned state so a late server value can't overwrite the pick.
  const confidenceOwnedRef = useRef(false);
  const mentalDemandOwnedRef = useRef(false);
  // Latch so a double Escape (or an Escape racing the unmount) can't emit two
  // survey_skipped events — onSkip must fire exactly once.
  const skippedRef = useRef(false);
  // Dialog container: initial focus on mount + focus restore on close.
  const dialogRef = useRef<HTMLDivElement>(null);

  const canSubmit = confidence !== null && mentalDemand !== null && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(confidence, mentalDemand);
    } catch (err) {
      // The only signal a real reader-feedback POST failed — never swallow it.
      logger.error("[study] survey submit failed", err);
      setSubmitting(false);
      setError("Submission failed — please try again.");
    }
  };

  // Move focus into the dialog on mount (visible focus + SR context) and
  // restore it to the previously-focused element when the survey closes.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (submitting) return;

      if (e.key === "Escape") {
        e.preventDefault();
        if (skippedRef.current) return;
        skippedRef.current = true;
        onSkip();
        return;
      }

      if (e.key === "Enter" && canSubmit) {
        e.preventDefault();
        void handleSubmit();
        return;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        setActiveField((f) => (f === "confidence" ? "mentalDemand" : "confidence"));
        return;
      }

      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= 5) {
        e.preventDefault();
        if (activeField === "confidence") {
          setConfidence(n);
          confidenceOwnedRef.current = true;
          // Auto-advance to mental demand after first confidence pick
          if (!mentalDemandOwnedRef.current) setActiveField("mentalDemand");
        } else {
          setMentalDemand(n);
          mentalDemandOwnedRef.current = true;
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSubmit is stable within the effect lifetime
  }, [submitting, activeField, canSubmit, onSkip]);

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[2px] outline-none"
      data-testid="study-post-case-survey"
      role="dialog"
      aria-modal="true"
      aria-label="Post-case ratings"
    >
      <div className="bg-bg-2 border border-border-strong rounded shadow-2xl px-8 py-7 max-w-lg w-full mx-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-signal-text-tint mb-2">
          Reader-time study — case complete
        </p>
        <h2 className="text-xl font-bold text-white mb-1">Two quick ratings</h2>
        <p className="text-sm text-fg-muted mb-6">
          Your ratings are required before moving to the next case.
        </p>

        {/* Confidence */}
        <RatingSection
          title="Confidence"
          question="How confident are you in the accuracy and completeness of your interpretation of this imaging study?"
          ratings={CONFIDENCE_RATINGS}
          selected={confidence}
          active={activeField === "confidence"}
          onSelect={(v) => {
            setConfidence(v);
            confidenceOwnedRef.current = true;
            if (!mentalDemandOwnedRef.current) setActiveField("mentalDemand");
          }}
          onClick={() => setActiveField("confidence")}
          data-testid="survey-confidence"
        />

        <div className="mt-5" />

        {/* Mental demand */}
        <RatingSection
          title="Mental demand"
          question="How mentally effortful was it to interpret this imaging study?"
          ratings={MENTAL_DEMAND_RATINGS}
          selected={mentalDemand}
          active={activeField === "mentalDemand"}
          onSelect={(v) => {
            setMentalDemand(v);
            mentalDemandOwnedRef.current = true;
          }}
          onClick={() => setActiveField("mentalDemand")}
          data-testid="survey-mental-demand"
        />

        {error !== null && (
          <p className="mt-3 text-sm text-red-400" data-testid="survey-error">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between mt-6">
          <p className="text-xs text-fg-faint">
            Press{" "}
            <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">
              1
            </kbd>
            –
            <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">
              5
            </kbd>{" "}
            to pick,{" "}
            <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">
              Tab
            </kbd>{" "}
            to switch,{" "}
            <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5">
              Enter
            </kbd>{" "}
            to submit
          </p>

          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className={
              "px-5 py-2 text-sm font-medium rounded-lg transition-colors " +
              (!canSubmit
                ? "bg-signal/30 text-signal-text-tint/50 cursor-not-allowed"
                : "bg-signal hover:bg-signal/90 text-white")
            }
            data-testid="survey-submit"
          >
            {submitting ? "Saving…" : "Submit & continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Inner component ───────────────────────────────────────────────────────────

interface RatingSectionProps {
  title: string;
  question: string;
  ratings: { value: number; label: string }[];
  selected: number | null;
  active: boolean;
  onSelect: (v: number) => void;
  onClick: () => void;
  "data-testid": string;
}

const RatingSection: React.FC<RatingSectionProps> = ({
  title,
  question,
  ratings,
  selected,
  active,
  onSelect,
  onClick,
  "data-testid": testId,
}) => (
  <div
    className={
      "rounded-lg border p-4 cursor-pointer transition-colors " +
      (active ? "border-signal-stroke bg-signal-tint/10" : "border-border-strong bg-surface-2/40")
    }
    onClick={onClick}
    data-testid={testId}
  >
    <p className="text-xs font-semibold uppercase tracking-wider text-fg-muted mb-1">{title}</p>
    <p className="text-sm text-white mb-4">{question}</p>
    <div className="flex items-stretch gap-2" role="radiogroup" aria-label={title}>
      {ratings.map((r) => {
        const isSelected = selected === r.value;
        return (
          <button
            key={r.value}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(r.value);
            }}
            className={
              "flex-1 flex flex-col items-center justify-center gap-1 py-3 rounded-lg border transition-colors " +
              (isSelected
                ? "border-signal-stroke bg-signal-tint text-white"
                : "border-border-strong bg-surface-2/60 text-fg-muted hover:bg-surface-2")
            }
            data-testid={`${testId}-option-${r.value}`}
            aria-pressed={isSelected}
          >
            <span className="text-lg font-mono font-bold">{r.value}</span>
            <span className="text-[10px] uppercase tracking-wide">{r.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);
